import React, { useState, useEffect, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { PDF_DOCUMENT_OPTIONS } from '../../pdfjsConfig';
import PinchZoom from './PinchZoom';

/** iOS WKWebView 여부 판별 — 캔버스 스케일 상한 적용에 사용 */
function isIOSPlatform() {
  try {
    return Capacitor.getPlatform() === 'ios';
  } catch {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
}
const IS_IOS = isIOSPlatform();

/** Chrome 등에서만 동작. iOS WKWebView는 대부분 없음 — GPU 메모리는 별도 */
function logTbmJsHeap(label) {
  if (typeof performance !== 'undefined' && performance.memory) {
    const m = performance.memory;
    const used = (m.usedJSHeapSize / 1048576).toFixed(2);
    const total = (m.totalJSHeapSize / 1048576).toFixed(2);
    const limit = (m.jsHeapSizeLimit / 1048576).toFixed(2);
    console.log(
      `[tbm][mem] ${label} — JS heap used=${used}MB total=${total}MB limit=${limit}MB`
    );
  } else {
    console.log(
      `[tbm][mem] ${label} — performance.memory 없음 (JS 힙 측정 불가, iOS WKWebView 일반적)`
    );
  }
}

const LoadingBox = ({ height = '80vh', size = 50, thickness = 6 }) => {
  return (
    <Box
      sx={{
        width: '100vw',
        height: height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress size={size} thickness={thickness} />
    </Box>
  );
};

const DocumentViewer = ({
  renderPreview,
  onDocumentLoadSuccess,
  pageNumber,
  pageWidth,
  pageViewHeight,
  setPageViewHeight,
  renderTextLayer = false,
  renderAnnotationLayer = false,
  onError,
}) => {
  const [error, setError] = useState(null);
  const [pageRenderReady, setPageRenderReady] = useState(false);
  const isMountedRef = useRef(true);

  const loadingHeight = pageViewHeight ? `${pageViewHeight}px` : '80vh';

  const dpr = window.devicePixelRatio || 1;

  /**
   * 1. 화면 픽셀 밀도(DPR) 값에 따라 multiplier 값 결정
   * 2. SE와 같은 저해상도(DPR 2), Pro와 같은 고해상도(DPR 3)
   * 3. iOS: totalScale 상한 2.0 — 큰 PDF 단계 전환 시 캔버스 메모리 피크·깜빡임 완화
   *    그 외: 상한 4.5 — 핀치 줌 확대 시 고해상도 유지
   */
  let multiplier = 1.5;
  if (dpr > 2) {
    multiplier = 1.5;
  } else {
    multiplier = 2.0;
  }

  const devicePixelRatio = window.devicePixelRatio || 1;
  const MAX_SCALE = IS_IOS ? 2.0 : 4.5;
  const totalScale = Math.min(devicePixelRatio * multiplier, MAX_SCALE);
  const renderWidth = pageWidth * totalScale;
  const initialScale = 1 / totalScale;

  useEffect(() => {
    if (isMountedRef.current) {
      setPageRenderReady(false);
      setError(null);
    }
  }, [renderPreview, pageNumber]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      const canvases = document.querySelectorAll(
        'canvas.react-pdf__Page__canvas'
      );
      let pixelSum = 0;
      canvases.forEach((canvas) => {
        pixelSum += canvas.width * canvas.height;
      });
      const canvasCount = canvases.length;
      const estGpuMB = ((pixelSum * 4) / 1048576).toFixed(2);

      logTbmJsHeap('DocumentViewer cleanup 직전');
      if (canvasCount > 0) {
        console.log(
          `[tbm][mem] 해제 대상 ${canvasCount}개 캔버스 픽셀합≈${pixelSum} → 이론 GPU ${estGpuMB}MB`
        );
      }

      canvases.forEach((canvas) => {
        // width=1로 설정하면 기존 대형 GPU 텍스처를 즉시 반환하도록 유도
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, 1, 1);
        canvas.remove();
      });

      setPageRenderReady(false);

      logTbmJsHeap('DocumentViewer cleanup 직후');
      requestAnimationFrame(() => {
        logTbmJsHeap('DocumentViewer cleanup 1프레임 후');
      });

      console.log(
        '[tbm] DocumentViewer 캔버스 해제 완료 (GPU 실제 반환 여부는 JS로 확인 불가, 위 heap은 참고용)'
      );
    };
  }, []);

  const handleLoadError = (err) => {
    console.error('[tbm] PDF 로드 에러:', err);
    if (isMountedRef.current) {
      setError(err);
      setPageRenderReady(true);
    }
    if (typeof onError === 'function') onError(err);
  };

  const handlePageRenderSuccess = () => {
    if (!isMountedRef.current) return;
    setPageRenderReady(true);
    try {
      const canvas = document.querySelector('canvas.react-pdf__Page__canvas');
      if (canvas && typeof setPageViewHeight === 'function') {
        const rect = canvas.getBoundingClientRect();
        setPageViewHeight(Math.ceil(rect.height));
        const estimatedMB = (
          (canvas.width * canvas.height * 4) /
          1024 /
          1024
        ).toFixed(2);
        console.log(
          `[tbm][Canvas] ${canvas.width}×${canvas.height}px ≈ GPU ${estimatedMB}MB (totalScale=${totalScale}, dpr=${dpr})`
        );
      }
    } catch (e) {
      console.error('[tbm] 캔버스 높이 측정 오류:', e);
    }
  };

  const handlePageRenderError = (err) => {
    console.error('[tbm] PDF 페이지 렌더 에러:', err);
    if (isMountedRef.current) {
      setError(err);
      setPageRenderReady(true);
    }
    if (typeof onError === 'function') onError(err);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: loadingHeight,
      }}
    >
      {!pageRenderReady && !error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
          }}
        >
          <LoadingBox height={loadingHeight} />
        </Box>
      )}
      <PinchZoom
        initialScale={initialScale}
        minScale={0.1}
        maxScale={totalScale * 5}
        wrapperStyle={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
        componentWrapperStyle={{
          width: '100%',
          height: '100%',
        }}
        contents={
          <Document
            file={renderPreview}
            options={PDF_DOCUMENT_OPTIONS}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={handleLoadError}
            error={
              <Typography sx={{ color: '#f44336', mt: 2 }}>
                PDF 파일을 로드할 수 없습니다.
              </Typography>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={renderWidth}
              renderTextLayer={renderTextLayer}
              renderAnnotationLayer={renderAnnotationLayer}
              onRenderSuccess={handlePageRenderSuccess}
              onRenderError={handlePageRenderError}
            />
          </Document>
        }
      />
    </Box>
  );
};

export default DocumentViewer;
